import Box from '@mui/material/Box';
import MenuItem from '@mui/material/MenuItem';
import { type ReactNode, useState } from 'react';
import { MenuSearchInput } from '../filters/MenuSearchInput.js';
import { filterOptionItemSx } from '../filters/menuStyles.js';
import { LookupControl } from './LookupControl.js';
import type { FieldOptionsResult } from './useFieldOptions.js';

interface LookupSelectProps {
  label: string;
  tooltip: string | null;
  resolved: FieldOptionsResult;
  value: string;
  hideLabel?: boolean;
  onChange: (value: string) => void;
}

export function LookupSelect({
  label,
  tooltip,
  resolved,
  value,
  hideLabel = false,
  onChange,
}: LookupSelectProps): ReactNode {
  const [query, setQuery] = useState('');
  const [expanded, setExpanded] = useState(false);
  const { options, more = [], onSearchChange } = resolved;
  // A search covers the whole list; the short lead list only applies while browsing.
  const all = [...options, ...more];
  const shown = query
    ? all.filter((option) => option.label.toLowerCase().includes(query.toLowerCase()))
    : expanded
      ? all
      : options;
  const canExpand = !query && !expanded && more.length > 0;

  return (
    <LookupControl
      label={label}
      tooltip={tooltip}
      chosen={all.find((option) => option.id === value)?.label}
      resolved={resolved}
      hideLabel={hideLabel}
    >
      {(close) => (
        <Box sx={{ minWidth: 240, maxWidth: 360 }}>
          <Box sx={{ px: 2, pt: 1, pb: 1 }}>
            <MenuSearchInput
              autoFocus
              placeholder={`Find ${label}…`}
              value={query}
              onChange={(text) => {
                setQuery(text);
                onSearchChange(text);
              }}
            />
          </Box>
          <Box sx={{ maxHeight: 280, overflowY: 'auto' }}>
            {shown.length === 0 ? (
              <Box sx={{ px: 2, py: 2, fontSize: 12, color: 'text.disabled' }}>No matches</Box>
            ) : (
              shown.map((option) => (
                <MenuItem
                  key={option.id}
                  selected={option.id === value}
                  sx={filterOptionItemSx}
                  onClick={() => {
                    onChange(option.id);
                    close();
                  }}
                >
                  {option.label}
                </MenuItem>
              ))
            )}
            {canExpand ? (
              <MenuItem
                sx={{ ...filterOptionItemSx, color: 'primary.main' }}
                onClick={() => setExpanded(true)}
              >
                See more
              </MenuItem>
            ) : null}
          </Box>
        </Box>
      )}
    </LookupControl>
  );
}
